<script setup lang="ts">
import { updateProfileSchema } from '@schwrz/shared';
import { ref, watch } from 'vue';

import { AppButton, AppFormField, AppHint, AppInput, UserAvatar } from '../../shared/ui';
import { useAuthStore } from '../../stores/auth';
import { usePushStore } from '../../stores/push';
import { useWorkspaceUsersStore } from '../../stores/workspaceUsers';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ close: [] }>();

const auth = useAuthStore();
const pushStore = usePushStore();
const workspaceUsers = useWorkspaceUsersStore();

const firstName = ref('');
const lastName = ref('');
const email = ref('');
const currentPassword = ref('');
const newPassword = ref('');
const submitting = ref(false);
const avatarUploading = ref(false);
const error = ref<string | null>(null);
const success = ref(false);
const avatarInput = ref<HTMLInputElement | null>(null);
const pushBusy = ref(false);
const pushError = ref<string | null>(null);
const showGameElements = ref(false);

function syncWorkspaceAvatar(): void {
  const me = auth.user;
  if (!me?.workspaceUserId) return;
  const existing = workspaceUsers.byId(me.workspaceUserId);
  if (!existing) return;
  workspaceUsers.items = workspaceUsers.items.map((u) =>
    u.id === me.workspaceUserId ? { ...u, avatarUrl: me.avatarUrl } : u,
  );
}

async function onAvatarPick(event: Event): Promise<void> {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = '';
  if (!file) return;

  error.value = null;
  success.value = false;
  avatarUploading.value = true;
  try {
    await auth.uploadAvatar(file);
    syncWorkspaceAvatar();
    success.value = true;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Не удалось загрузить фото';
  } finally {
    avatarUploading.value = false;
  }
}

async function onRemoveAvatar(): Promise<void> {
  error.value = null;
  success.value = false;
  avatarUploading.value = true;
  try {
    await auth.removeAvatar();
    syncWorkspaceAvatar();
    success.value = true;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Не удалось удалить фото';
  } finally {
    avatarUploading.value = false;
  }
}

function openAvatarPicker(): void {
  avatarInput.value?.click();
}

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return;
    firstName.value = auth.user?.firstName ?? '';
    lastName.value = auth.user?.lastName ?? '';
    email.value = auth.user?.email ?? '';
    showGameElements.value = auth.user?.showGameElements ?? false;
    currentPassword.value = '';
    newPassword.value = '';
    error.value = null;
    success.value = false;
    pushError.value = null;
    void pushStore.refreshStatus();
  },
);

async function togglePush(): Promise<void> {
  pushError.value = null;
  pushBusy.value = true;
  try {
    if (pushStore.subscribed) {
      await pushStore.disable();
    } else {
      const reason = await pushStore.enable();
      if (reason) pushError.value = reason;
    }
  } catch (err) {
    pushError.value = err instanceof Error ? err.message : 'Не удалось изменить push-уведомления';
  } finally {
    pushBusy.value = false;
  }
}

async function onSubmit(): Promise<void> {
  error.value = null;
  success.value = false;
  const payload: Record<string, string | boolean> = {
    firstName: firstName.value.trim(),
    lastName: lastName.value.trim(),
    email: email.value.trim(),
  };
  if (auth.user?.gamificationEnabled) {
    payload['showGameElements'] = showGameElements.value;
  }
  if (newPassword.value) {
    payload['currentPassword'] = currentPassword.value;
    payload['newPassword'] = newPassword.value;
  }
  const parsed = updateProfileSchema.safeParse(payload);
  if (!parsed.success) {
    error.value = parsed.error.issues[0]?.message ?? 'Проверьте поля';
    return;
  }
  submitting.value = true;
  try {
    await auth.updateProfile(parsed.data);
    success.value = true;
    currentPassword.value = '';
    newPassword.value = '';
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Не удалось сохранить';
  } finally {
    submitting.value = false;
  }
}
</script>

<template>
  <div
    v-if="open"
    class="profile-dialog__backdrop"
    @click.self="emit('close')"
  >
    <div
      class="profile-dialog"
      role="dialog"
      aria-label="Профиль"
    >
      <header class="profile-dialog__head">
        <h2>Профиль</h2>
        <button
          type="button"
          class="btn btn--ghost btn--icon"
          aria-label="Закрыть"
          @click="emit('close')"
        >
          ×
        </button>
      </header>
      <form
        class="form"
        @submit.prevent="onSubmit"
      >
        <fieldset :disabled="submitting || avatarUploading">
          <div class="profile-dialog__avatar">
            <UserAvatar
              :first-name="firstName"
              :last-name="lastName"
              :avatar-url="auth.user?.avatarUrl"
              size="lg"
            />
            <div class="profile-dialog__avatar-actions">
              <input
                ref="avatarInput"
                type="file"
                class="profile-dialog__avatar-input"
                accept="image/jpeg,image/png,image/webp,image/gif"
                @change="onAvatarPick"
              />
              <AppButton
                type="button"
                variant="ghost"
                :loading="avatarUploading"
                @click="openAvatarPicker"
              >
                Загрузить
              </AppButton>
              <AppButton
                v-if="auth.user?.avatarUrl"
                type="button"
                variant="ghost"
                :disabled="avatarUploading"
                @click="onRemoveAvatar"
              >
                Удалить
              </AppButton>
            </div>
          </div>

          <div class="profile-dialog__grid">
            <AppFormField label="Имя">
              <AppInput
                v-model="firstName"
                type="text"
                required
              />
            </AppFormField>
            <AppFormField label="Фамилия">
              <AppInput
                v-model="lastName"
                type="text"
              />
            </AppFormField>
            <AppFormField
              class="profile-dialog__grid-full"
              label="Email"
            >
              <AppInput
                v-model="email"
                type="email"
                required
              />
            </AppFormField>
          </div>

          <div
            v-if="pushStore.supported"
            class="profile-dialog__push"
          >
            <span class="profile-dialog__push-label">
              Push
              <span
                v-if="pushStore.subscribed"
                class="profile-dialog__push-on"
              >вкл.</span>
            </span>
            <AppButton
              type="button"
              :variant="pushStore.subscribed ? 'ghost' : 'primary'"
              :loading="pushBusy"
              @click="togglePush"
            >
              {{ pushStore.subscribed ? 'Отключить' : 'Включить' }}
            </AppButton>
          </div>
          <AppHint
            v-if="pushStore.supported && pushError"
            variant="danger"
            class="profile-dialog__push-hint"
          >
            {{ pushError }}
          </AppHint>

          <label
            v-if="auth.user?.gamificationEnabled"
            class="trigger-row profile-dialog__game-toggle"
          >
            <div class="trigger-row__event">
              Игровой режим (баллы и прогресс)
            </div>
            <input
              v-model="showGameElements"
              type="checkbox"
              class="trigger-row__switch"
              aria-label="Игровой режим (баллы и прогресс)"
            />
          </label>

          <p class="hint profile-dialog__section-label">
            Пароль (необязательно)
          </p>
          <div class="profile-dialog__grid">
            <AppFormField label="Текущий">
              <AppInput
                v-model="currentPassword"
                type="password"
                autocomplete="current-password"
              />
            </AppFormField>
            <AppFormField label="Новый (мин. 9)">
              <AppInput
                v-model="newPassword"
                type="password"
                autocomplete="new-password"
              />
            </AppFormField>
          </div>

          <div class="form__actions">
            <AppButton
              type="submit"
              variant="primary"
              :loading="submitting"
            >
              Сохранить
            </AppButton>
            <AppButton
              type="button"
              variant="ghost"
              @click="emit('close')"
            >
              Закрыть
            </AppButton>
          </div>
          <AppHint
            v-if="error"
            variant="danger"
          >
            {{ error }}
          </AppHint>
          <p
            v-else-if="success"
            class="hint profile-dialog__ok"
          >
            ✓ Сохранено
          </p>
        </fieldset>
      </form>
    </div>
  </div>
</template>

<style scoped>
.profile-dialog__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 6vh;
  z-index: 120;
}
.profile-dialog {
  width: min(420px, 92vw);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.4);
}
.profile-dialog__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.55rem 0.75rem;
  border-bottom: 1px solid var(--border);
}
.profile-dialog__head h2 {
  margin: 0;
  font-size: 0.95rem;
}
.profile-dialog .form {
  padding: 0.65rem 0.75rem 0.75rem;
}
.profile-dialog .form :deep(.form-field) {
  margin-bottom: 0.4rem;
}
.profile-dialog .form :deep(.form__actions) {
  margin-top: 0.55rem;
}
.profile-dialog__ok {
  margin: 0.35rem 0 0;
  font-size: 0.78rem;
  color: var(--accent);
}
.profile-dialog__avatar {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.55rem;
}
.profile-dialog__avatar-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.2rem;
}
.profile-dialog__avatar-input {
  display: none;
}
.profile-dialog__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0 0.55rem;
}
.profile-dialog__grid-full {
  grid-column: 1 / -1;
}
.profile-dialog__section-label {
  margin: 0.45rem 0 0.25rem;
  font-size: 0.75rem;
}
.profile-dialog__push {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.35rem;
  padding: 0.35rem 0;
  border-top: 1px solid var(--border);
}
.profile-dialog__push-label {
  font-size: 0.78rem;
  color: var(--muted);
}
.profile-dialog__push-on {
  margin-left: 0.25rem;
  color: var(--accent);
}
.profile-dialog__push-hint {
  margin-top: 0.2rem;
}
.profile-dialog__game-toggle {
  margin-top: 0.35rem;
  padding: 0.35rem 0;
  border-top: 1px solid var(--border);
}
</style>
